import React, { useRef, useState, useEffect } from 'react'
import { useStepLoriStyles } from './styles'
import { Container } from '../../../../components'
import {
    Paper,
    Typography,
    TextField,
    IconButton,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import { LoriText } from '../../../../navigation/layouts/Lori/Icons/LoriText'
import { useLoriState } from '../../../../contexts/LoriContext'
import { TypingIndicator } from '../../../../navigation/layouts/Lori/Channels/LoriChannelContent/TypingIndicator'
import { useLoriVoice } from '../../../../hooks/useLoriVoice'
import { ApartmentVendor, Folder, ezScheduleDetails } from '../../../../models'
import { useLocation } from 'react-router-dom'
import MicIcon from '@material-ui/icons/Mic'
import StopIcon from '@material-ui/icons/Stop'
import { SchedulePhase } from './types'
import { LoriReview } from './components/LoriReview'
import SendIcon from '@material-ui/icons/Send'
import ReplayIcon from '@material-ui/icons/Replay'
import { SetTemplateUnitsParams } from '../../../../hooks/useEzSchedule/types'
import { AdvancedLoriWelcome } from './components/AdvancedLoriWelcome'
import { AdvancedLoriTree } from './components/AdvancedLoriTree'
import { AdvancedLoriServices } from './components/AdvancedLoriServices'
import { ExistingDataGate } from './components/ExistingDataGate'

interface Props {
    infrastructure: Folder
    onPhaseChange?: (phase: SchedulePhase) => void
    ezSchedule: ezScheduleDetails
    buildTurnProjectButton: boolean
    loriReviewButton: boolean
    retryPreviousMessageButton: boolean
    setTemplateUnits: (params: SetTemplateUnitsParams) => void
    getPreviousTurnsButton: boolean
    apartmentVendorList: ApartmentVendor[]
    templateSchedules: ezScheduleDetails[]
    templateSchedulesLoading: boolean
    templateSchedulesError: unknown
    phaseId: number
    ezScheduleFetching?: boolean
    onGoBack: () => void
    resetPhase: () => Promise<unknown>
}

export const AdvancedLori = (props: Props) => {
    const classes = useStepLoriStyles()
    const theme = useTheme()
    const inputRef = useRef<HTMLInputElement>(null)
    const chatMessagesRef = useRef<HTMLDivElement>(null)
    const location = useLocation()
    const {
        handleSendMessage,
        connectionReady,
        activeConversation,
        thinking,
        setThinking,
    } = useLoriState()

    const [localInput, setLocalInput] = useState<string>('')

    const activeURL = location.pathname
    const { recording, startRecording, stopRecording } = useLoriVoice(activeURL)

    const filteredMessages =
        activeConversation?.chat_messages?.filter(
            (msg) =>
                msg.role !== 'system' &&
                (msg.role === 'user' ||
                    (msg.message_items?.[0]?.value?.trim() ?? '') !== ''),
        ) ?? []

    useEffect(() => {
        const container = chatMessagesRef.current
        if (!container) return

        const scrollToBottom = () => {
            requestAnimationFrame(() => {
                container.scrollTop = container.scrollHeight
            })
        }

        scrollToBottom()

        const observer = new MutationObserver(scrollToBottom)
        observer.observe(container, { childList: true, subtree: true })

        return () => observer.disconnect()
    }, [])

    const handleBuildTurnProject = () => {
        props.onPhaseChange?.('review')
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            if (localInput.trim() && connectionReady) {
                handleSendMessage(localInput, window.location.pathname)
                setLocalInput('')
            }
        }
    }

    const handleSendClick = () => {
        if (localInput.trim() && connectionReady) {
            handleSendMessage(localInput, window.location.pathname)
            setLocalInput('')
        }
    }

    // Need to add some logic to include a system message to indicate to the agent that this
    // message caused failure in the previous attempt
    const handleRetryPreviousMessage = () => {
        // Need to find last user message and send it again
        const lastUserMessage = activeConversation?.chat_messages?.filter(
            (msg) => msg.role === 'user',
        )?.[
            activeConversation?.chat_messages?.filter(
                (msg) => msg.role === 'user',
            )?.length - 1
        ]
        if (lastUserMessage) {
            handleSendMessage(
                lastUserMessage.message_items?.[0]?.value ?? '',
                window.location.pathname,
            )
        }
    }

    return (
        <ExistingDataGate
            ezSchedule={props.ezSchedule}
            phaseId={props.phaseId}
            onReset={props.resetPhase}
            onExit={props.onGoBack}
        >
            <Container className={classes.advancedLoriLayout}>
                <div className={classes.treePanel}>
                    <AdvancedLoriTree infrastructure={props.infrastructure} />
                </div>
                <Paper
                    elevation={0}
                    className={`${classes.chatWindow} ${classes.advancedChatWindow}`}
                >
                    {/* Header */}
                    <div className={classes.headerRow}>
                        <div className={classes.chatHeader}>
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'baseline',
                                    justifyContent: 'center',
                                    whiteSpace: 'nowrap',
                                    gap: 6,
                                }}
                            >
                                <span>Advanced Turn with</span>
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        lineHeight: 0,
                                        transform: 'translateY(2px)',
                                    }}
                                >
                                    <LoriText width={86} height={30} />
                                </span>
                            </span>
                        </div>
                    </div>

                    {/* Message List */}
                    <div ref={chatMessagesRef} className={classes.chatMessages}>
                        {/* Welcome Message */}
                        <AdvancedLoriWelcome />
                        {/* Chat Messages */}
                        {filteredMessages.length ? (
                            filteredMessages.map((msg, idx) => (
                                <div
                                    key={idx}
                                    className={
                                        msg.role === 'user'
                                            ? classes.userMsg
                                            : classes.botMsg
                                    }
                                >
                                    {msg.message_items?.[0]?.value}
                                </div>
                            ))
                        ) : (
                            <div>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    align="center"
                                >
                                    One moment while we connect to Lori...
                                </Typography>
                            </div>
                        )}
                        {/* Thinking Indicator */}
                        {thinking && <TypingIndicator theme={theme} />}

                        {/* Lori Review */}
                        {props.loriReviewButton && (
                            <LoriReview
                                ezSchedule={props.ezSchedule}
                                onProceedToReview={handleBuildTurnProject}
                                phaseId={props.phaseId}
                                isLoading={props.ezScheduleFetching}
                            />
                        )}
                    </div>

                    {/* Input/Footer - fixed at bottom */}
                    <div className={classes.footerContainer}>
                        {/* Text/Voice Input Row */}
                        <div className={classes.chatInputRow}>
                            <TextField
                                className={classes.input}
                                placeholder="Input value here or use the above selectors..."
                                variant="outlined"
                                size="small"
                                value={localInput}
                                onChange={(e) => setLocalInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                inputRef={inputRef}
                                InputProps={{
                                    classes: {
                                        root: classes.inputRoot,
                                    },
                                }}
                                disabled={!connectionReady}
                            />
                            <div className={classes.inputButtons}>
                                {/* Send Button */}
                                {props.retryPreviousMessageButton ? (
                                    <Tooltip title="Retry Previous Message">
                                        <IconButton
                                            disabled={!connectionReady}
                                            color={
                                                connectionReady
                                                    ? 'primary'
                                                    : 'default'
                                            }
                                            onClick={handleRetryPreviousMessage}
                                        >
                                            <ReplayIcon
                                                htmlColor={
                                                    theme.palette.text.secondary
                                                }
                                            />
                                        </IconButton>
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="Send Message (Enter)">
                                        <IconButton
                                            onClick={handleSendClick}
                                            disabled={!connectionReady}
                                            color={
                                                connectionReady
                                                    ? 'primary'
                                                    : 'default'
                                            }
                                        >
                                            <SendIcon
                                                htmlColor={
                                                    theme.palette.text.secondary
                                                }
                                            />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {/* Mic Button */}
                                <Tooltip title="Record Message">
                                    <IconButton
                                        onClick={
                                            recording
                                                ? () => {
                                                      stopRecording()
                                                      setThinking(true)
                                                  }
                                                : startRecording
                                        }
                                        color={
                                            recording ? 'secondary' : 'default'
                                        }
                                        disabled={!connectionReady}
                                    >
                                        {recording && (
                                            <>
                                                <span
                                                    className={classes.micPulse}
                                                />
                                                <StopIcon />
                                            </>
                                        )}
                                        {!recording && <MicIcon />}
                                    </IconButton>
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                </Paper>
                <div className={classes.servicesPanel}>
                    <AdvancedLoriServices
                        apartmentVendorList={props.apartmentVendorList}
                    />
                </div>
            </Container>
        </ExistingDataGate>
    )
}
